import { useEffect, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import api from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { Badge } from '@/components/ui/badge';
import {
  Disc3,
  Music,
  Lock,
  AlertCircle,
  ExternalLink,
  Share2,
} from 'lucide-react';
import { useAuth } from '@/context/AuthContext';

/**
 * SharedView — renders a login-gated shared card or crate.
 *
 * URL: /s/:token
 * If not authenticated -> nudges to Login (with ?next= redirect).
 * If authenticated     -> fetches /api/share/:token and renders payload.
 */
export default function SharedView() {
  const { token } = useParams();
  const navigate = useNavigate();
  const { user, loading: authLoading } = useAuth();
  const [state, setState] = useState('loading'); // loading | ready | not_found | error
  const [data, setData] = useState(null);

  const backend = process.env.REACT_APP_BACKEND_URL;
  const jwt = typeof window !== 'undefined' ? localStorage.getItem('groovlens_token') : null;
  const coverUrl = data?.crate_cover_path
    ? `${backend}/api/files/${data.crate_cover_path}${jwt ? `?auth=${encodeURIComponent(jwt)}` : ''}`
    : null;

  useEffect(() => {
    // Wait for auth check to settle before deciding.
    if (authLoading) return;
    if (!user) return; // login-wall handled below
    let cancelled = false;
    (async () => {
      try {
        const { data: resp } = await api.get(`/share/${token}`);
        if (cancelled) return;
        setData(resp);
        setState('ready');
      } catch (e) {
        if (cancelled) return;
        if (e?.response?.status === 404) setState('not_found');
        else setState('error');
      }
    })();
    return () => { cancelled = true; };
  }, [token, user, authLoading]);

  // ---- Login wall --------------------------------------------------------
  if (!authLoading && !user) {
    const next = encodeURIComponent(`/s/${token}`);
    return (
      <div className="max-w-md mx-auto py-16 px-4 text-center">
        <div className="mx-auto w-14 h-14 rounded-2xl bg-secondary grid place-items-center mb-4">
          <Lock className="w-6 h-6 text-[hsl(var(--primary))]" />
        </div>
        <h1 className="font-display text-2xl font-semibold">Sign in to view</h1>
        <p className="text-sm text-muted-foreground mt-2">
          Someone shared their GroovLens crate with you. Sign in to your account (or create one) to see it.
        </p>
        <div className="mt-6 flex flex-col sm:flex-row gap-2 justify-center">
          <Button
            onClick={() => navigate(`/login?next=${next}`)}
            className="rounded-lg"
            data-testid="shared-view-login"
          >
            Sign in
          </Button>
          <Button
            variant="outline"
            onClick={() => navigate(`/signup?next=${next}`)}
            className="rounded-lg"
            data-testid="shared-view-signup"
          >
            Create account
          </Button>
        </div>
      </div>
    );
  }

  if (state === 'loading' || authLoading) {
    return (
      <div className="max-w-3xl mx-auto py-6 space-y-3">
        <Skeleton className="h-8 w-1/2" />
        <Skeleton className="h-48 w-full" />
        <Skeleton className="h-14 w-full" />
        <Skeleton className="h-14 w-full" />
      </div>
    );
  }

  if (state === 'not_found') {
    return (
      <div className="max-w-md mx-auto py-16 text-center">
        <AlertCircle className="w-10 h-10 mx-auto text-muted-foreground" />
        <h1 className="font-display text-xl font-semibold mt-3">Share not found</h1>
        <p className="text-sm text-muted-foreground mt-1">
          This link was revoked, expired, or never existed.
        </p>
        <Button variant="outline" className="mt-6 rounded-lg" onClick={() => navigate('/crate')}>
          Back to your crate
        </Button>
      </div>
    );
  }

  if (state === 'error') {
    return (
      <div className="max-w-md mx-auto py-16 text-center">
        <AlertCircle className="w-10 h-10 mx-auto text-[hsl(var(--destructive))]" />
        <h1 className="font-display text-xl font-semibold mt-3">Something went wrong</h1>
        <p className="text-sm text-muted-foreground mt-1">Try again in a moment.</p>
      </div>
    );
  }

  // ---- Ready: render card or crate --------------------------------------
  const owner = data.owner_username;

  if (data.kind === 'card' && data.card) {
    const c = data.card;
    const links = [
      { label: 'iTunes', url: c.itunes_url },
      { label: 'Spotify', url: c.spotify_url },
      { label: 'YouTube', url: c.youtube_url },
      { label: 'Beatport', url: c.beatport_url },
      { label: 'Bandcamp', url: c.bandcamp_url },
    ].filter((l) => !!l.url);

    return (
      <div className="max-w-lg mx-auto py-6 px-4" data-testid="shared-view-card">
        <div className="flex items-center gap-2 mb-4 text-xs text-muted-foreground">
          <Share2 className="w-3.5 h-3.5" />
          <span>
            <span className="text-foreground font-medium">@{owner}</span> shared this track with you
          </span>
        </div>

        <div className="rounded-2xl border border-border bg-card overflow-hidden">
          <div className="aspect-square w-full bg-secondary grid place-items-center">
            {c.album_art_url ? (
              <img src={c.album_art_url} alt="" className="w-full h-full object-cover" />
            ) : (
              <Disc3 className="w-16 h-16 text-muted-foreground" />
            )}
          </div>
          <div className="p-4">
            {c.genre_bucket && (
              <Badge variant="outline" className="text-[10px] mb-2">
                {c.genre_bucket}
              </Badge>
            )}
            <h1 className="font-display text-2xl font-semibold leading-tight">
              {c.title || 'Untitled'}
            </h1>
            <p className="text-sm text-muted-foreground mt-0.5">{c.artist}</p>
            {c.album && (
              <p className="text-xs text-muted-foreground mt-1">from {c.album}{c.year ? ` \u00b7 ${c.year}` : ''}</p>
            )}

            {c.preview_url && (
              <audio controls src={c.preview_url} className="w-full mt-3 rounded" />
            )}

            {links.length > 0 && (
              <div className="grid grid-cols-2 gap-2 mt-4">
                {links.map((l) => (
                  <Button
                    key={l.label}
                    variant="outline"
                    asChild
                    className="rounded-lg justify-start"
                  >
                    <a href={l.url} target="_blank" rel="noopener noreferrer">
                      <ExternalLink className="w-3.5 h-3.5 mr-2" /> {l.label}
                    </a>
                  </Button>
                ))}
              </div>
            )}
          </div>
        </div>

        <div className="mt-4 flex justify-center">
          <Link to="/crate" className="text-xs text-muted-foreground hover:text-foreground underline underline-offset-4">
            Open your GroovLens crate
          </Link>
        </div>
      </div>
    );
  }

  // Crate view
  return (
    <div className="max-w-3xl mx-auto py-6 px-4" data-testid="shared-view-crate">
      <div className="flex items-center gap-2 mb-4 text-xs text-muted-foreground">
        <Share2 className="w-3.5 h-3.5" />
        <span>
          <span className="text-foreground font-medium">@{owner}</span> shared this crate with you
        </span>
      </div>

      <div className="rounded-2xl border border-border bg-card overflow-hidden mb-4">
        <div className="aspect-[3/1] w-full bg-secondary relative">
          {coverUrl ? (
            <img src={coverUrl} alt="" className="w-full h-full object-cover" />
          ) : (
            <div className="w-full h-full grid place-items-center">
              <Music className="w-10 h-10 text-muted-foreground" />
            </div>
          )}
        </div>
        <div className="p-4">
          <h1 className="font-display text-3xl font-semibold leading-tight">
            {data.crate_bucket} crate
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            {data.crate_cards?.length || 0} tracks
          </p>
        </div>
      </div>

      <ul className="space-y-2" data-testid="shared-crate-tracklist">
        {(data.crate_cards || []).map((c) => (
          <li
            key={c.id}
            className="rounded-xl border border-border bg-card p-3 flex items-center gap-3"
          >
            <div className="w-12 h-12 rounded-lg overflow-hidden bg-secondary grid place-items-center shrink-0">
              {c.album_art_url ? (
                <img src={c.album_art_url} alt="" className="w-full h-full object-cover" />
              ) : (
                <Disc3 className="w-5 h-5 text-muted-foreground" />
              )}
            </div>
            <div className="flex-1 min-w-0">
              <div className="font-medium truncate">{c.title || 'Untitled'}</div>
              <div className="text-xs text-muted-foreground truncate">{c.artist}</div>
            </div>
            {c.itunes_url && (
              <Button variant="ghost" asChild size="icon" className="rounded-lg shrink-0">
                <a href={c.itunes_url} target="_blank" rel="noopener noreferrer" aria-label="Open on iTunes">
                  <ExternalLink className="w-4 h-4" />
                </a>
              </Button>
            )}
          </li>
        ))}
      </ul>

      <div className="mt-6 flex justify-center">
        <Link to="/crate" className="text-xs text-muted-foreground hover:text-foreground underline underline-offset-4">
          Open your GroovLens crate
        </Link>
      </div>
    </div>
  );
}
